import { useQuery } from '@apollo/react-hooks';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { GET_DATA_QUERY } from './gql.tsx';
import type {
	getPublicFormChangeboardingData,
	getPublicFormChangeboardingDataVariables,
} from './__generated_apollo__/getPublicFormChangeboardingData.ts';

const getProjectHasFormNavItem = (data: getPublicFormChangeboardingData) => {
	if (!data.jira?.containerNavigation) {
		return false;
	}

	if (!('navigationItems' in data.jira.containerNavigation)) {
		return false;
	}

	return (
		data.jira.containerNavigation.navigationItems?.edges?.some(
			(node) => node?.node?.typeKey === 'FORMS',
		) ?? false
	);
};

const getProjectHasPublicForm = (data: getPublicFormChangeboardingData) => {
	return (
		data.jiraBusinessFormsByProjectId?.nodes?.some((node) => node?.accessLevel === 'public') ??
		false
	);
};

export const useGetData = ({
	projectId,
	isEnabled,
}: {
	projectId: string;
	isEnabled?: boolean;
}) => {
	const cloudId = useCloudId();
	const projectAri = JiraProjectAri.create({
		siteId: cloudId,
		projectId,
	});

	const { data } = useQuery<
		getPublicFormChangeboardingData,
		getPublicFormChangeboardingDataVariables
	>(GET_DATA_QUERY, {
		variables: {
			projectId,
			projectAri: projectAri.toString(),
		},
		fetchPolicy: 'network-only',
		skip: !isEnabled,
	});

	if (!data) {
		return undefined;
	}

	const projectHasFormNavItem = getProjectHasFormNavItem(data);
	const isPublicFormEnabledForSite = data.jiraBusinessFormsByProjectId?.publicFormsEnabled ?? null;
	const canAdministerProject = data.jira?.jiraProject?.administerProject?.canPerform ?? false;
	const projectHasPublicForm = getProjectHasPublicForm(data);
	const firstStoryLevelIssueTypeId =
		data.jira?.jiraProject?.issueTypes?.edges?.[0]?.node?.issueTypeId ?? null;

	return {
		forms: data.jiraBusinessFormsByProjectId?.nodes,
		project: data.jira?.jiraProject,
		projectHasFormNavItem,
		isPublicFormEnabledForSite,
		canAdministerProject,
		projectHasPublicForm,
		firstStoryLevelIssueTypeId,
	};
};
